import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getOrderById } from '../../services/order.service';
import { createAdditionalPayment } from '../../services/payment.service';
import { useAuthStore } from '../../store/authStore';
import { Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';

const OrderPaymentPage = () => {
  const { orderId, paymentIndex } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!accessToken) {
      navigate('/clients/signin');
    }
  }, [accessToken, navigate]);

  const { data, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrderById(orderId),
    enabled: !!orderId && !!accessToken,
  });

  if (!accessToken) {
    return null;
  }

  const order = data?.order;
  const paymentInfo = order?.additionalPayments?.[parseInt(paymentIndex, 10)];

  const handlePayWithPayPal = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await createAdditionalPayment({ orderId, paymentIndex: parseInt(paymentIndex, 10) });
      window.location.href = result.approvalUrl;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 size={36} className="animate-spin text-[#174568]" />
      </div>
    );
  }

  if (!order || !paymentInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">Payment request not found.</p>
          <button onClick={() => navigate(-1)} className="text-[#174568] underline">Go back</button>
        </div>
      </div>
    );
  }

  if (paymentInfo.status === 'paid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-green-500 text-lg mb-4">This payment has already been completed.</p>
          <button onClick={() => navigate('/clients/projects')} className="text-[#174568] underline">View Projects</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 font-['Poppins']">
      <div className="max-w-lg mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          Back
        </button>

        <div className="bg-white rounded-md overflow-hidden">
          <div className="bg-[#174568] px-6 py-5">
            <h1 className="text-white text-xl font-semibold">Additional Payment</h1>
            <p className="text-blue-100 text-sm mt-1">Review your payment request</p>
          </div>

          <div className="flex items-center gap-4 p-5 border-b">
            {order.img && (
              <img
                src={order.img}
                alt={order.title}
                className="w-20 h-16 rounded-md object-cover flex-shrink-0"
              />
            )}
            <div>
              <p className="text-[#174568] font-semibold text-sm leading-tight line-clamp-2">Order: {order.title}</p>
              <span className="inline-block mt-1 text-xs bg-blue-50 text-[#174568] px-2 py-0.5 rounded-md">
                Order #{order._id.substring(order._id.length - 6).toUpperCase()}
              </span>
            </div>
          </div>

          <div className="p-5 space-y-3 border-b">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Reason</span>
              <span className="font-medium text-right max-w-[60%]">{paymentInfo.description}</span>
            </div>
            
            <div className="flex justify-between text-base font-semibold border-t pt-3 mt-3">
              <span>Amount Due</span>
              <span className="text-[#174568] text-lg">${paymentInfo.amount?.toFixed(2)}</span>
            </div>
          </div>

          <div className="p-5">
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handlePayWithPayPal}
              disabled={loading}
              className="w-full bg-[#0070BA] text-white py-3.5 rounded-md font-semibold text-base hover:bg-[#005ea6] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Redirecting to PayPal…
                </>
              ) : (
                <>
                  <img
                    src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg"
                    alt="PayPal"
                    className="h-5 rounded"
                  />
                  Pay with PayPal
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-2 mt-4 text-gray-400 text-xs">
              <ShieldCheck size={14} />
              <span>Secured by PayPal — your payment info is never stored on our servers.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderPaymentPage;
